import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { plans } from '../data/plans';
import { API_CONFIG } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import PaymentMethodsRow from '../components/PaymentMethodsRow';
import StripeCheckoutModal from '../components/modals/StripeCheckoutModal';
import { SubscriptionSuccessModal } from '../components/modals/SubscriptionSuccessModal';
import { getSubscriptionPlans, SubscriptionPlan } from '../services/credits';
import { useTracking } from '../hooks/useTracking';
import { IS_PRODUCTION, IS_DEVELOPMENT } from '../config/environment';
import { cacheManager, CACHE_KEYS, CACHE_TTL } from '../services/cacheManager';

export default function UpgradePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { openAuthModal, isOpen: isAuthModalOpen, closeAuthModal } = useAuthModal();
  const { track } = useTracking();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const pageLoadTimeRef = useRef<number>(Date.now());

  // Stripe key validation function
  const validateStripeKey = (publishableKey: string): boolean => {
    const isLiveKey = publishableKey.startsWith('pk_live_');
    const isTestKey = publishableKey.startsWith('pk_test_');
    
    // Valid combinations:
    // 1. Production environment + Live key ✅
    // 2. Development environment + Test key ✅
    
    // Invalid combinations:
    // 3. Production environment + Test key ⚠️ (warn but allow)
    // 4. Development environment + Live key ❌ (error)
    
    if (IS_DEVELOPMENT && isLiveKey) {
      console.error('[Stripe] ❌ CRITICAL: Using LIVE Stripe key in development!');
      console.error('[Stripe] This could cause real charges. Please use test keys in dev.');
      return false;
    }
    
    if (IS_PRODUCTION && isTestKey) {
      console.warn('[Stripe] ⚠️ WARNING: Using TEST Stripe key in production.');
      console.warn('[Stripe] Real payments will fail. Please configure live keys.');
      // Allow but warn
    }
    
    if (IS_PRODUCTION && isLiveKey) {
      // Correct configuration
    }
    
    if (IS_DEVELOPMENT && isTestKey) {
      // Correct configuration
    }
    
    return true;
  };
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanIdState] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // Use regular state management instead of useOptimisticData to avoid Suspense
  const [stripePlansResponse, setStripePlansResponse] = useState<{ plans: SubscriptionPlan[] } | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Convert plans array to map for easy lookup
  const stripePlans: Record<string, SubscriptionPlan> = React.useMemo(() => {
    if (!stripePlansResponse) return {};
    const plansMap: Record<string, SubscriptionPlan> = {};
    stripePlansResponse.plans.forEach((plan) => {
      plansMap[plan.id] = plan;
    });
    return plansMap;
  }, [stripePlansResponse]);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(1); // Default to Pro (index 1)
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // CRITICAL: Clear stale OAuth flags to prevent random redirects from /upgrade
  // This is a revenue-protection measure - users on checkout must not be redirected
  useEffect(() => {
    if (isAuthenticated) {
      try {
        sessionStorage.removeItem('oauth_initiated_time');
        sessionStorage.removeItem('oauth_provider');
        console.log('[UpgradePage] Cleared stale OAuth flags to prevent redirect');
      } catch (e) {
        console.error('[UpgradePage] Failed to clear OAuth flags:', e);
      }
    }
  }, [isAuthenticated]);

  // Load subscription plans without Suspense (prevents error #426)
  useEffect(() => {
    // Load from cache first (synchronous, immediate)
    const cached = cacheManager.get<{ plans: SubscriptionPlan[] }>(CACHE_KEYS.SUBSCRIPTION_PLANS);
    if (cached) {
      console.log('[UpgradePage] Using cached subscription plans');
      setStripePlansResponse(cached);
    }
    
    // Then fetch fresh data in background (async, non-blocking)
    setLoadingPlans(true);
    getSubscriptionPlans()
      .then(response => {
        console.log('[UpgradePage] Fetched fresh subscription plans');
        setStripePlansResponse(response);
        // Cache will be updated by the API service
      })
      .catch(error => {
        console.error('[UpgradePage] Failed to load subscription plans:', error);
        // Don't create fake fallback plans - could break Stripe payments
        // If no cached data, user will see loading state or default plan cards
      })
      .finally(() => setLoadingPlans(false));
  }, []);

  // Detect back navigation and force immediate cached data (prevents Suspense on back nav)
  useEffect(() => {
    const handlePopState = () => {
      console.log('[UpgradePage] Back navigation detected, loading cached data immediately');
      const cached = cacheManager.get<{ plans: SubscriptionPlan[] }>(CACHE_KEYS.SUBSCRIPTION_PLANS);
      if (cached) {
        setStripePlansResponse(cached);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Refactored subscribe function that takes planId as parameter
  const handleSubscribeWithPlan = useCallback(async (planId?: string) => {
    const planToUse = planId || selectedPlan;
    
    if (!planToUse) {
      setError('Please select a plan first');
      return;
    }

    if (!user) {
      setError('Please sign in to subscribe');
      return;
    }

    // Check if user has email (required for Stripe)
    if (!user.email) {
      setError('Please sign in with an email address to subscribe. Anonymous users cannot subscribe.');
      return;
    }

    setIsSubscribing(true);
    setError(null);

    try {
      const apiBaseUrl = API_CONFIG.BASE_URL;

      // Plan IDs match backend (basic, pro, premium)
      const planIdToUse = planToUse;
      
      // Validate plan ID
      const validPlans = ['basic', 'pro', 'premium'];
      if (!validPlans.includes(planIdToUse)) {
        throw new Error(`Invalid plan: ${planIdToUse}. Please contact support.`);
      }

      // Create SetupIntent for PaymentElement (native form fields)
      const response = await fetch(`${apiBaseUrl}/api/stripe/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: planIdToUse,
          billing_period: billingPeriod,
          email: user.email,
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Backend error:', errorData);
        throw new Error(errorData.error || `Failed to create setup intent (${response.status})`);
      }

      const data = await response.json();
      
      if (data.success && data.client_secret && data.customer_id) {
        // Store setup intent and customer info for PaymentElement
        setCheckoutClientSecret(data.client_secret);
        setCustomerId(data.customer_id);
        setSelectedPlanIdState(planIdToUse);
        setSelectedPlan(planIdToUse);
        
        // Store publishable key from backend response (if provided)
        if (data.publishable_key) {
          const receivedKey = data.publishable_key;
          
          // Validate the Stripe key against the environment
          if (!validateStripeKey(receivedKey)) {
            setError('Stripe configuration error: Invalid key for this environment. Please contact support.');
            setIsSubscribing(false);
            return;
          }
          
          setStripePublishableKey(receivedKey);
        }
        
        setShowCheckout(true);
        
        // Track begin_checkout event
        const plan = plans.find((p) => p.id === planIdToUse);
        const stripePlan = stripePlans[planIdToUse];
        // Always use monthly equivalent for conversion value
        const monthlyEquivalentPrice = billingPeriod === 'annual' 
          ? (stripePlan?.price_annual_usd || 0) / 12 
          : (stripePlan?.price_monthly_usd || 0);
        
        track('begin_checkout', {
          plan_id: planIdToUse,
          plan_name: plan?.name || planIdToUse,
          plan_price: monthlyEquivalentPrice,
          currency: 'USD',
          value: monthlyEquivalentPrice, // Always monthly equivalent
        });
      } else {
        throw new Error('No setup intent client secret returned');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process subscription');
      setIsSubscribing(false);
    }
  }, [billingPeriod, user, selectedPlan, stripePlans, track]);

  // Handle auth success - proceed with pending plan if exists
  useEffect(() => {
    if (isAuthenticated && pendingPlan && user) {
      // User just authenticated, proceed with the pending plan
      setSelectedPlan(pendingPlan);
      const planToProcess = pendingPlan;
      setPendingPlan(null);
      // Trigger subscribe flow
      handleSubscribeWithPlan(planToProcess);
    }
  }, [isAuthenticated, pendingPlan, user, handleSubscribeWithPlan]);

  // Clear pending plan if auth modal closes without authentication
  useEffect(() => {
    if (!isAuthModalOpen && !isAuthenticated && pendingPlan) {
      // Modal closed without auth, clear pending plan
      setPendingPlan(null);
    }
  }, [isAuthModalOpen, isAuthenticated, pendingPlan]);

  // OPTION A: Prevent auth modal from causing redirects for already-authenticated users
  // Close auth modal immediately if user is authenticated (prevents stale OAuth state issues)
  useEffect(() => {
    if (isAuthenticated && isAuthModalOpen) {
      console.log('[UpgradePage] 🛡️ Closing auth modal for already-authenticated user (preventing auto-redirect)');
      track('upgrade_page_auth_modal_closed_for_authenticated', {
        user_id: user?.id,
        time_on_page_ms: Date.now() - pageLoadTimeRef.current,
      });
      closeAuthModal();
    }
  }, [isAuthenticated, isAuthModalOpen, closeAuthModal, track, user?.id]);

  // FIX 3: Clear lander-1 modal state when /upgrade page mounts (defensive cleanup)
  // This prevents processing modals from incorrectly appearing on /upgrade
  useEffect(() => {
    const lander1ModalState = localStorage.getItem('genanything_lander1_modal_state');
    if (lander1ModalState) {
      console.log('[UpgradePage] 🧹 Clearing stale lander-1 modal state on mount');
      localStorage.removeItem('genanything_lander1_modal_state');
    }
  }, []); // Run once on mount

  // FIX 4: Clear stale OAuth flags for authenticated users (prevents OAuth loops)
  // These flags shouldn't exist if user is already authenticated on /upgrade
  useEffect(() => {
    if (isAuthenticated) {
      const oauthInitiated = sessionStorage.getItem('genanything_oauth_initiated');
      const oauthProvider = sessionStorage.getItem('genanything_oauth_provider');
      
      if (oauthInitiated || oauthProvider) {
        console.log('[UpgradePage] 🧹 Clearing stale OAuth flags for authenticated user');
        sessionStorage.removeItem('genanything_oauth_initiated');
        sessionStorage.removeItem('genanything_oauth_provider');
        
        track('upgrade_page_oauth_flags_cleared', {
          user_id: user?.id,
          had_oauth_initiated: !!oauthInitiated,
          had_oauth_provider: !!oauthProvider,
        });
      }
    }
  }, [isAuthenticated, track, user?.id]);

  // Track view_content when page loads - global deduplication in useTracking will prevent duplicates
  useEffect(() => {
    track('view_content', {
      content_type: 'upgrade_page',
      page_path: '/upgrade',
    });
  }, [track]);

  // Track page unload and time on page
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeOnPage = Date.now() - pageLoadTimeRef.current;
      track('upgrade_page_unload', {
        time_on_page_ms: timeOnPage,
        user_id: user?.id,
        is_authenticated: isAuthenticated,
      });
    };

    // Track on unmount (navigation away)
    return () => {
      const timeOnPage = Date.now() - pageLoadTimeRef.current;
      track('upgrade_page_exit', {
        time_on_page_ms: timeOnPage,
        user_id: user?.id,
        is_authenticated: isAuthenticated,
      });
    };
  }, [track, user?.id, isAuthenticated]);

  // Track Facebook browser back button/gestures
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const timeOnPage = Date.now() - pageLoadTimeRef.current;
      const isFacebookBrowser = /FBAN|FBAV/i.test(navigator.userAgent);
      
      console.log('[UpgradePage] Browser back button detected', {
        timeOnPage,
        isFacebookBrowser,
      });
      
      track('upgrade_page_back_button', {
        time_on_page_ms: timeOnPage,
        is_facebook_browser: isFacebookBrowser,
        is_mobile: /Android|iPhone|iPad/i.test(navigator.userAgent),
        user_agent: navigator.userAgent,
        user_id: user?.id,
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [track, user?.id]);

  // Plans are now loaded via useOptimisticData hook above
  // No need for manual fetch effect

  // Set initial scroll position to Pro plan (middle card) without animation
  useEffect(() => {
    if (!carouselRef.current || hasUserScrolled) return;
    
    // Find Pro plan index
    const proIndex = plans.findIndex(p => p.id === 'pro');
    if (proIndex === -1) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const container = carouselRef.current;
      if (!container) return;
      
      const cards = container.querySelectorAll('[data-plan-card]');
      if (cards[proIndex]) {
        // Calculate scroll position to center the Pro plan card
        // This only affects horizontal scroll, not page vertical scroll
        const card = cards[proIndex] as HTMLElement;
        const containerWidth = container.offsetWidth;
        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const scrollLeft = cardLeft - (containerWidth / 2) + (cardWidth / 2);
        
        // Directly set scrollLeft instead of using scrollIntoView
        // This prevents any vertical page scrolling
        container.scrollLeft = scrollLeft;
      }
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on initial mount

  // Track user scroll to prevent auto-scrolling after manual interaction and update active index
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!hasUserScrolled) {
        setHasUserScrolled(true);
      }

      // Update active index based on scroll position
      const cards = container.querySelectorAll('[data-plan-card]');
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach((card, idx) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - containerCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = idx;
        }
      });

      setActiveCarouselIndex(closestIndex);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasUserScrolled]);

  // New function to handle plan selection with auth check
  const handlePlanClick = async (planId: string) => {
    // Track add_to_cart event
    const plan = plans.find((p) => p.id === planId);
    const stripePlan = stripePlans[planId];
    // Always use monthly equivalent for conversion value
    const monthlyEquivalentPrice = billingPeriod === 'annual' 
      ? (stripePlan?.price_annual_usd || 0) / 12 
      : (stripePlan?.price_monthly_usd || 0);
    
    track('add_to_cart', {
      plan_id: planId,
      plan_name: plan?.name || planId,
      plan_price: monthlyEquivalentPrice,
      currency: 'USD',
      value: monthlyEquivalentPrice, // Always monthly equivalent
    });

    // If not authenticated, open auth modal and store pending plan
    if (!isAuthenticated || !user) {
      setPendingPlan(planId);
      openAuthModal({
        afterAuthRedirect: '/upgrade',
        reason: 'select-plan',
        fromPath: '/upgrade',
        onCancelBehavior: { type: 'stay' },
        postAuthAction: () => {
          // This will be handled by the useEffect above
        },
      });
      return;
    }

    // If authenticated, proceed directly
    setSelectedPlan(planId);
    await handleSubscribeWithPlan(planId);
  };

  return (
    <div className="min-h-screen bg-bg">
      <div className="fluid-container py-8">
        {/* Global Error Banner */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <p>{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Choose Your Plan Section */}
        <div className="text-center mb-16">
          <h2 className="fluid-h2 font-bold text-white mb-6">Join 50,000+ Users</h2>

          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white/5 border border-white/20 rounded-lg p-1 inline-flex">
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-black'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Annual (Save 50%)
              </button>
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-black'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          {/* Plan Cards - Carousel on mobile/tablet, Grid on desktop */}
          {/* Mobile/Tablet Carousel (below lg) */}
          <div className="lg:hidden mb-16 relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
            <div
              ref={carouselRef}
              className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 px-4 pb-4"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
              onKeyDown={(e) => {
                if (!carouselRef.current) return;
                const container = carouselRef.current;
                if (e.key === 'ArrowLeft') {
                  container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' });
                } else if (e.key === 'ArrowRight') {
                  container.scrollBy({ left: container.clientWidth, behavior: 'smooth' });
                }
              }}
              tabIndex={0}
            >
              {plans.map((plan) => {
                const isPremium = plan.id === 'premium';
                return (
                  <div
                    key={`${plan.id}-${billingPeriod}`}
                    data-plan-card
                    className="flex-shrink-0 snap-center snap-always w-[calc(100vw-2rem)]"
                  >
                    <div
                      className={`bg-white/5 border rounded-2xl p-6 flex flex-col h-full transition-all ${
                        selectedPlan === plan.id
                          ? 'border-white/50 bg-white/10 shadow-lg'
                          : 'border-white/20 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                  {/* Header Section */}
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                    {plan.popular && (
                      <div className="inline-block bg-blue-500/20 border border-blue-500/50 text-blue-400 px-3 py-1 rounded-full text-xs font-semibold">
                        {plan.badgeText}
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">
                        {loadingPlans ? (
                          '...'
                        ) : (() => {
                          const stripePlan = stripePlans[plan.id];
                          if (!stripePlan) {
                            return '...';
                          }
                          // For annual, show monthly equivalent (price_annual_usd / 12) as main price
                          // For monthly, show the actual monthly price
                          const price = billingPeriod === 'annual' 
                            ? stripePlan.price_annual_usd / 12 
                            : stripePlan.price_monthly_usd;
                          return `$${price.toFixed(0)}`;
                        })()}
                      </span>
                      <span className="text-white/70">/month</span>
                      {/* Slashed out price */}
                      {!loadingPlans && (() => {
                        const slashedPrices: Record<string, Record<string, number>> = {
                          'annual': { 'basic': 30, 'pro': 60, 'premium': 120 },
                          'monthly': { 'basic': 40, 'pro': 80, 'premium': 160 }
                        };
                        const slashedPrice = slashedPrices[billingPeriod]?.[plan.id];
                        if (slashedPrice) {
                          return (
                            <span className="text-2xl font-bold text-white/40 line-through ml-1">
                              ${slashedPrice}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {billingPeriod === 'annual' && (
                      <p className="text-xs text-white/60 mt-2 text-left">
                        {loadingPlans ? (
                          '...'
                        ) : (() => {
                          const stripePlan = stripePlans[plan.id];
                          if (!stripePlan) {
                            return '...';
                          }
                          return `Billed $${stripePlan.price_annual_usd.toFixed(0)}/year`;
                        })()}
                      </p>
                    )}
                    {billingPeriod === 'monthly' && (
                      <p className="text-xs text-white/60 mt-2 text-left">
                        Billed monthly
                      </p>
                    )}
                  </div>

                  {/* Features List - Flex-1 to push footer down */}
                  <div className="flex-1">
                    <ul className="space-y-3 mb-6 text-left">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-white/80 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Footer Section - mt-auto to align across cards */}
                  <div className="mt-auto pt-4 border-t border-white/10">
                    <PaymentMethodsRow variant="compact" align="center" />
                    <button
                      onClick={() => handlePlanClick(plan.id)}
                      disabled={isSubscribing}
                      className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 rounded-full font-semibold hover:bg-[#E7D7B1] transition-colors mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubscribing && selectedPlan === plan.id ? 'Processing...' : 'Select Plan'}
                    </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            {/* Pagination Dots - Mobile/Tablet only */}
            <div className="flex justify-center gap-2 mt-4">
              {plans.map((plan, index) => (
                <button
                  key={plan.id}
                  onClick={() => {
                    const container = carouselRef.current;
                    if (!container) return;
                    const cards = container.querySelectorAll('[data-plan-card]');
                    if (cards[index]) {
                      cards[index].scrollIntoView({ behavior: 'smooth', inline: 'center' });
                      setHasUserScrolled(true);
                    }
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    activeCarouselIndex === index
                      ? 'bg-white w-6'
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to ${plan.name} plan`}
                />
              ))}
            </div>
          </div>

          {/* Desktop Grid (lg and above) */}
          <div className="hidden lg:grid grid-cols-3 gap-6 mb-16 items-stretch">
            {plans.map((plan) => {
              const isPremium = plan.id === 'premium';
              return (
                <div
                  key={`${plan.id}-${billingPeriod}`}
                  className={`bg-white/5 border rounded-2xl p-6 flex flex-col h-full transition-all ${
                    selectedPlan === plan.id
                      ? 'border-white/50 bg-white/10 shadow-lg'
                      : 'border-white/20 hover:border-white/30 hover:bg-white/5'
                  }`}
                >
                  {/* Header Section */}
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                    {plan.popular && (
                      <div className="inline-block bg-blue-500/20 border border-blue-500/50 text-blue-400 px-3 py-1 rounded-full text-xs font-semibold">
                        {plan.badgeText}
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">
                        {loadingPlans ? (
                          '...'
                        ) : (() => {
                          const stripePlan = stripePlans[plan.id];
                          if (!stripePlan) {
                            return '...';
                          }
                          // For annual, show monthly equivalent (price_annual_usd / 12) as main price
                          // For monthly, show the actual monthly price
                          const price = billingPeriod === 'annual' 
                            ? stripePlan.price_annual_usd / 12 
                            : stripePlan.price_monthly_usd;
                          return `$${price.toFixed(0)}`;
                        })()}
                      </span>
                      <span className="text-white/70">/month</span>
                      {/* Slashed out price */}
                      {!loadingPlans && (() => {
                        const slashedPrices: Record<string, Record<string, number>> = {
                          'annual': { 'basic': 30, 'pro': 60, 'premium': 120 },
                          'monthly': { 'basic': 40, 'pro': 80, 'premium': 160 }
                        };
                        const slashedPrice = slashedPrices[billingPeriod]?.[plan.id];
                        if (slashedPrice) {
                          return (
                            <span className="text-2xl font-bold text-white/40 line-through ml-1">
                              ${slashedPrice}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {billingPeriod === 'annual' && (
                      <p className="text-xs text-white/60 mt-2 text-left">
                        {loadingPlans ? (
                          '...'
                        ) : (() => {
                          const stripePlan = stripePlans[plan.id];
                          if (!stripePlan) {
                            return '...';
                          }
                          return `Billed $${stripePlan.price_annual_usd.toFixed(0)}/year`;
                        })()}
                      </p>
                    )}
                    {billingPeriod === 'monthly' && (
                      <p className="text-xs text-white/60 mt-2 text-left">
                        Billed monthly
                      </p>
                    )}
                  </div>

                  {/* Features List - Flex-1 to push footer down */}
                  <div className="flex-1">
                    <ul className="space-y-3 mb-6 text-left">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-white/80 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Footer Section - mt-auto to align across cards */}
                  <div className="mt-auto pt-4 border-t border-white/10">
                    <PaymentMethodsRow variant="compact" align="center" />
                    <button
                      onClick={() => handlePlanClick(plan.id)}
                      disabled={isSubscribing}
                      className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 rounded-full font-semibold hover:bg-[#E7D7B1] transition-colors mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubscribing && selectedPlan === plan.id ? 'Processing...' : 'Select Plan'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stripe Checkout Modal */}
        <StripeCheckoutModal
          open={showCheckout && !!checkoutClientSecret}
          onClose={() => {
            setShowCheckout(false);
            setCheckoutClientSecret(null);
            setCustomerId(null);
            setSelectedPlan(null);
            setSelectedPlanIdState(null);
            setStripePublishableKey(null);
            setIsSubscribing(false);
          }}
          clientSecret={checkoutClientSecret}
          customerId={customerId}
          planId={selectedPlanId}
          billingPeriod={billingPeriod}
          publishableKey={stripePublishableKey}
          userId={user?.id}
          onComplete={() => {
            setShowCheckout(false);
            setCheckoutClientSecret(null);
            setCustomerId(null);
            setSelectedPlan(null);
            setSelectedPlanIdState(null);
            setIsSubscribing(false);
            setShowSuccessModal(true);
          }}
          onError={(error) => {
            console.error('Checkout error:', error);
            setError(error.message || 'Checkout failed. Please try again.');
            setIsSubscribing(false);
          }}
        />
      </div>

      {/* Subscription Success Modal */}
      {showSuccessModal && (
        <SubscriptionSuccessModal
          onClose={() => {
            setShowSuccessModal(false);
            window.location.href = '/my-collection';
          }}
        />
      )}
    </div>
  );
}

